import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	auditMediaContentType,
	getMediaContentTypeRepairStatus,
	startMediaContentTypeRepair,
	stopMediaContentTypeRepair,
} from "../../api/apiServiceFetch";

// Совпадает с умолчаниями на сервере (MediaContentTypeRepairService).
const DEFAULT_SCAN = 2000;
const MAX_SCAN = 50000;

// Сколько неверных объектов показывать примером в отчёте проверки.
const SAMPLE_SIZE = 10;

// Как часто спрашиваем сервер о ходе прогона. Две секунды — так числа заметно
// шевелятся, а запросов при этом немного.
const POLL_MS = 2000;

const toNumber = (value, fallback) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const formatTime = (value) => {
	if (!value) return "";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString();
};

/**
 * Разовая починка Content-Type у старых файлов в хранилище.
 *
 * Файлы, загруженные примерно до 30 августа, лежат с типом
 * application/octet-stream вместо image/webp и прочего. Всё, что берёт ссылку
 * снаружи и верит заголовкам (Telegram, превью ссылок, кеши), такой файл
 * отклоняет. Новые загрузки уже правильные — чинится только прошлое.
 *
 * Одно нажатие делает всю работу: прогон идёт до конца папки сам, партий нет.
 * Ждать ответа на один запрос нельзя — обход сотни тысяч файлов занимает
 * минуты, — поэтому запуск, ход и остановка это три коротких запроса, а ход
 * прогона карточка переспрашивает раз в пару секунд.
 *
 * Проверка осталась необязательным предпросмотром: она ничего не меняет, и
 * починка её результатов не требует и не стирает.
 */
const MediaContentTypeCard = () => {
	const [prefix, setPrefix] = useState("");
	const [scanLimit, setScanLimit] = useState(DEFAULT_SCAN);

	const [isAuditing, setIsAuditing] = useState(false);
	const [audit, setAudit] = useState(null);
	const [auditError, setAuditError] = useState(null);

	const [status, setStatus] = useState(null);
	const [statusError, setStatusError] = useState(null);
	const [isStarting, setIsStarting] = useState(false);
	const [isStopping, setIsStopping] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);

	// Карточку могли закрыть и открыть снова — запросы уже улетевшего экрана не
	// должны трогать состояние.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const refreshStatus = useCallback(async () => {
		try {
			const data = await getMediaContentTypeRepairStatus();
			if (!mounted.current) return data;
			setStatus(data);
			setStatusError(null);
			return data;
		} catch (error) {
			console.error("Media content type status error:", error);
			if (mounted.current) setStatusError(error?.message || "Запрос не удался");
			return null;
		}
	}, []);

	const isRunning = status?.state === "running";

	// Первый опрос при открытии: прогон мог быть запущен раньше — с другой
	// вкладки или до перезагрузки страницы. Владелец должен увидеть его ход, а
	// не пустую карточку.
	useEffect(() => {
		refreshStatus();
	}, [refreshStatus]);

	// Пока прогон идёт — переспрашиваем; как только он кончился, опрос сам
	// прекращается.
	useEffect(() => {
		if (!isRunning) return undefined;
		const timer = setInterval(refreshStatus, POLL_MS);
		return () => clearInterval(timer);
	}, [isRunning, refreshStatus]);

	const scan = toNumber(scanLimit, DEFAULT_SCAN);

	const handleAudit = async () => {
		setIsAuditing(true);
		setAuditError(null);
		try {
			const data = await auditMediaContentType({
				prefix: prefix.trim(),
				maxObjects: Math.min(scan, MAX_SCAN),
				sample: SAMPLE_SIZE,
			});
			if (mounted.current) setAudit(data);
		} catch (error) {
			console.error("Audit media content type error:", error);
			if (mounted.current) {
				setAudit(null);
				setAuditError(error?.message || "Запрос не удался");
			}
		} finally {
			if (mounted.current) setIsAuditing(false);
		}
	};

	const handleStart = async () => {
		setIsStarting(true);
		setIsConfirming(false);
		setStatusError(null);
		try {
			const data = await startMediaContentTypeRepair({ prefix: prefix.trim() });
			if (mounted.current) setStatus(data);
		} catch (error) {
			console.error("Start media content type repair error:", error);
			// Самый вероятный отказ — «прогон уже идёт» (409). Спросим, что там на
			// самом деле, и покажем его ход вместо ошибки.
			if (mounted.current) setStatusError(error?.message || "Запрос не удался");
			await refreshStatus();
		} finally {
			if (mounted.current) setIsStarting(false);
		}
	};

	const handleStop = async () => {
		setIsStopping(true);
		try {
			const data = await stopMediaContentTypeRepair();
			if (mounted.current) setStatus(data);
		} catch (error) {
			console.error("Stop media content type repair error:", error);
			if (mounted.current) setStatusError(error?.message || "Запрос не удался");
		} finally {
			if (mounted.current) setIsStopping(false);
		}
	};

	const counters = status
		? `Просмотрено:             ${status.scanned}
Починено:                ${status.repaired}
Тип уже был верный:      ${status.skippedCorrect}
Расширение неизвестно:   ${status.skippedUnknownExtension} (не тронуты)
Не удалось:              ${status.failed}`
		: "";

	return (
		<section className="settings-page__card">
			<h3>Тип файлов в хранилище (Content-Type)</h3>
			<p className="settings-page__hint">
				Файлы, загруженные примерно до 30 августа, лежат в хранилище с типом
				<b> application/octet-stream</b> вместо <b>image/webp</b> и прочих. В
				приложении и на сайте это незаметно, но всё, что берёт ссылку снаружи и
				верит заголовкам — Telegram, превью ссылок в мессенджерах, кеши, —
				такой файл отклоняет. Новые загрузки уже правильные: чинится только
				прошлое.
			</p>
			<p className="settings-page__hint">
				Починка переписывает у файла только заголовки, на стороне хранилища.
				Содержимое не скачивается и не загружается заново, ссылки не меняются,
				доступ к файлу остаётся прежним.
			</p>

			<div className="settings-page__row">
				<label className="settings-page__label">Папка (префикс ключа)</label>
				<input
					type="text"
					value={prefix}
					placeholder="например, Resources/Photo/545776/ — пусто значит всё хранилище"
					disabled={isRunning || isStarting}
					onChange={(e) => {
						setPrefix(e.target.value);
						setAudit(null);
						setIsConfirming(false);
					}}
				/>
			</div>

			<p className="settings-page__hint">
				Начинать разумно с одной папки: так видно, что числа сходятся, прежде
				чем трогать всё хранилище.
			</p>

			<p className="settings-page__hint">
				Тип файла определяется по его расширению. Расширения, которых нет в
				списке приложения, не трогаются вовсе — тип для них не угадывается, они
				считаются отдельно.
			</p>

			{/* Починка. Одно нажатие доводит папку до конца — считать партии и
			    нажимать по десять раз больше не надо. */}
			<div className="settings-page__row">
				<button
					type="button"
					disabled={isRunning || isStarting || isConfirming}
					onClick={() => {
						setStatusError(null);
						setIsConfirming(true);
					}}
				>
					{isRunning ? "Починка идёт..." : "Починить всё"}
				</button>
				{isRunning ? (
					<button
						type="button"
						className="settings-page__button--secondary"
						disabled={isStopping || status?.stopRequested}
						onClick={handleStop}
					>
						{status?.stopRequested ? "Останавливается..." : "Остановить"}
					</button>
				) : (
					<p className="settings-page__hint settings-page__hint--beside">
						Прогон идёт до конца папки сам. Страницу можно закрыть — он не
						прервётся, а числа вернутся, когда вы откроете её снова.
					</p>
				)}
			</div>

			{isConfirming && (
				<div className="settings-page__confirm">
					<p className="settings-page__confirm-text">
						Будут проверены все файлы
						{prefix.trim() ? ` в папке ${prefix.trim()}` : " во всём хранилище"},
						и каждому, у кого тип не соответствует расширению, тип будет
						проставлен. Содержимое файлов не меняется, ссылки остаются
						прежними.
					</p>
					<p className="settings-page__confirm-text">
						Это надолго: на каждый файл уходит отдельный запрос к хранилищу.
						Ход виден здесь же и обновляется сам. Остановить можно в любой
						момент — это безопасно, а следующий запуск продолжит с места
						остановки: уже починенные файлы он пропустит.
					</p>
					<div className="settings-page__row">
						<button type="button" disabled={isStarting} onClick={handleStart}>
							{isStarting ? "Запускаем..." : "Да, починить всё"}
						</button>
						<button
							type="button"
							className="settings-page__button--secondary"
							onClick={() => setIsConfirming(false)}
						>
							Отмена
						</button>
					</div>
				</div>
			)}

			{statusError && (
				<p className="settings-page__status is-error">{statusError}</p>
			)}

			{status && status.state !== "idle" && (
				<div className="settings-page__result">
					{isRunning && (
						<p className="settings-page__status is-ok">
							Починка идёт
							{status.prefix ? ` в папке ${status.prefix}` : " во всём хранилище"}
							{status.startedAtUtc ? `, с ${formatTime(status.startedAtUtc)}` : ""}.
							{status.stopRequested
								? " Останавливаемся: доделываем текущий файл."
								: ""}
						</p>
					)}

					{status.state === "done" && (
						<p className="settings-page__status is-ok">
							Готово: дошли до конца
							{status.prefix ? ` папки ${status.prefix}` : " хранилища"} —
							чинить больше нечего.
						</p>
					)}

					{status.state === "stopped" && (
						<p className="settings-page__status is-ok">
							Остановлено на полпути. Всё, что успели, починено; остальное
							ждёт. Нажмите «Починить всё» ещё раз — прогон продолжит с места
							остановки, уже починенные файлы он пропустит.
						</p>
					)}

					{status.state === "failed" && (
						<p className="settings-page__status is-error">
							Прогон прервался ошибкой: {status.error || "неизвестно"}. Всё, что
							было починено до этого, осталось починенным — можно запустить
							снова.
						</p>
					)}

					<pre className="settings-page__answer">{counters}</pre>

					{isRunning && status.lastKey && (
						<p className="settings-page__hint">
							Сейчас на файле: {status.lastKey}
						</p>
					)}

					{status.failures?.length > 0 && (
						<>
							<p className="settings-page__status is-error">
								Эти файлы остались как были и попадут в следующий прогон:
							</p>
							<pre className="settings-page__answer">
								{status.failures
									.map((item) => `${item.key}\n    ${item.error}`)
									.join("\n")}
							</pre>
						</>
					)}
				</div>
			)}

			{/* Проверка — необязательный предпросмотр. Починка её не требует и её
			    числа не стирает: пересчитывать ради продолжения не нужно. */}
			<hr className="settings-page__divider" />

			<p className="settings-page__hint">
				Проверка ничего не меняет и для починки не нужна — она только
				показывает, сколько неверных файлов в начале папки, если хочется
				посмотреть заранее.
			</p>

			<div className="settings-page__row">
				<label className="settings-page__label">Смотреть не больше</label>
				<input
					type="number"
					min="1"
					max={MAX_SCAN}
					value={scanLimit}
					disabled={isAuditing}
					onChange={(e) => setScanLimit(e.target.value)}
				/>
				<span className="settings-page__hint settings-page__hint--inline">
					файлов за одну проверку (до {MAX_SCAN})
				</span>
			</div>

			<div className="settings-page__row">
				<button type="button" disabled={isAuditing} onClick={handleAudit}>
					{isAuditing ? "Проверка идёт..." : "Проверить"}
				</button>
				<p className="settings-page__hint settings-page__hint--beside">
					{isAuditing
						? "Ответ может идти до минуты, не закрывайте страницу."
						: "Проверка ничего не меняет — запускайте сколько угодно раз."}
				</p>
			</div>

			{auditError && (
				<p className="settings-page__status is-error">{auditError}</p>
			)}

			{audit && (
				<div className="settings-page__result">
					<p className="settings-page__status is-ok">
						Просмотрено файлов: <b>{audit.scanned}</b>
						{audit.prefix ? ` в папке ${audit.prefix}` : " во всём хранилище"}.
					</p>
					<pre className="settings-page__answer">
						{`Тип верный:              ${audit.correct}
Тип неверный:            ${audit.wrong}
Расширение неизвестно:   ${audit.unknownExtension} (не трогаем)
Не удалось прочитать:    ${audit.failed}`}
					</pre>

					{!audit.scanComplete && (
						<p className="settings-page__hint">
							Это не всё: проверка остановилась на пределе в {audit.scanned}{" "}
							файлов, дальше есть ещё. Починку это не ограничивает — она идёт
							до конца папки.
						</p>
					)}

					{audit.scanComplete && audit.wrong === 0 && (
						<p className="settings-page__status is-ok">
							Чинить нечего: у всех просмотренных файлов тип верный.
						</p>
					)}

					{audit.sample?.length > 0 && (
						<>
							<p className="settings-page__hint">
								Примеры неверных (первые {audit.sample.length}):
							</p>
							<pre className="settings-page__answer">
								{audit.sample
									.map(
										(item) =>
											`${item.key}\n    сейчас: ${
												item.currentContentType || "(пусто)"
											}  →  нужно: ${item.intendedContentType}`,
									)
									.join("\n")}
							</pre>
						</>
					)}

					{audit.failures?.length > 0 && (
						<>
							<p className="settings-page__status is-error">
								Не удалось прочитать {audit.failed} файл(ов):
							</p>
							<pre className="settings-page__answer">
								{audit.failures
									.map((item) => `${item.key}\n    ${item.error}`)
									.join("\n")}
							</pre>
						</>
					)}
				</div>
			)}
		</section>
	);
};

export default MediaContentTypeCard;
